import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToasterService, LayoutBreakpointService } from '@headpower/layout';
import { BlahService } from '@headpower/blah-ng';
import { EmptyError, takeUntil, Subject } from 'rxjs';
import { ParagraphComponent } from '../../../models/paragraph-component/paragraph-component';
import { ResourceEntry, ResourceService } from 'src/app/modules/shared/services/resource.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ZoomPictureDialogComponent } from 'src/app/modules/shared/components/zoom-picture-dialog/zoom-picture-dialog.component';

interface FileEntry {
    name: string;
    nameEncoded: string;
    icon: string;
    path: string;
    pathEncoded: string;
    size: number;
}

@Component({
    selector: 'app-files-paragraph',
    templateUrl: './files-paragraph.component.html',
    styleUrls: ['./files-paragraph.component.scss'],
    standalone: false
})

export class FilesParagraphComponent implements OnInit, ParagraphComponent, OnDestroy {

    @Input() public data: any;
    @ViewChild('pictureDialogTemplate') dialogTemplate: any;
    dialogRef: MatDialogRef<any> | null = null;

    public aliasEncoded!: string;
    public mobile: boolean = false;
    public files: FileEntry[] = [];
    public images: any[] = [];
    public loadingFile?: number;
    public objectPicture: any
    public showPictureIndex: number = 0;
    public onlyPictures: boolean = false;
    public selectedValue: any;

    private touchStartX: number = 0;
    private touchStartY: number = 0;
    private destroy$: Subject<void> = new Subject();

    constructor(
        private layoutBreakpointService: LayoutBreakpointService,
        private resourceService: ResourceService,
        private toaster: ToasterService,
        private blahService: BlahService,
        private dialog: MatDialog) { }

    ngOnInit() {
        this.layoutBreakpointService.observer$
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => {
                this.mobile = result.handset;
            });

        this.aliasEncoded = encodeURIComponent(this.data.full.data.attributes.path.alias.substring(1));

        this.files = this.buildFileList();
        this.onlyPictures = this.files.every(file => file.icon === 'image');
        this.createImages();
    }

    public async downloadFile(file: FileEntry, fileIndex: number) {
        if (isFinite(this.loadingFile)) {
            if (this.loadingFile !== fileIndex) {
                this.toaster.create(this.blahService.blah('default.previousDownloadInProgress'));
            }
            return;
        }

        this.loadingFile = fileIndex;

        let resource: ResourceEntry;

        try {
            resource = await this.resourceService.getResource('file', file.path, file.name);
        }
        catch (error) {
            this.loadingFile = undefined;

            // EmptyError is thrown if service disposes the resources while request is in progress.
            //
            // This happens when user navigates away from the instruction while downloading,
            // so just ignore the error and return.
            if (error instanceof EmptyError) {
                return;
            }

            this.toaster.create(this.blahService.blah('default.errorWhileDownloadingFile'), {
                color: 'warn'
            });

            console.error(error);
            return;
        }
        this.loadingFile = undefined;

        const link = document.createElement('a');
        link.href = resource.objectUrl;
        link.download = file.name;
        link.dispatchEvent(new MouseEvent('click'));
    }

    public async createImages() {
        //ensure the order will be the same always
        const promises = this.files.map(async file => {
            try {
                const resource = await this.resourceService.getResource('file', file.path, file.name);
                return resource?.objectUrl ?? null;
            }
            catch (error) {
                if (error instanceof EmptyError) {
                    return null;
                }

                this.toaster.create(this.blahService.blah('default.errorWhileDownloadingFile'), {
                    color: 'warn'
                });
                console.error(error);
                return null;
            }
        });
        const results = await Promise.all(promises);
        this.images = results.filter(url => url !== null) as any[];

        if (this.onlyPictures && this.images.length > 0) {
            this.showPictureIndex = 0;
            this.selectedValue = this.images[this.showPictureIndex];
        }
    }

    public onTouchStart(event: TouchEvent) {
        this.touchStartX = event.changedTouches[0].screenX;
        this.touchStartY = event.changedTouches[0].screenY;
    }

    public onTouchMove(event: TouchEvent) {
        const deltaX = Math.abs(event.changedTouches[0].screenX - this.touchStartX);
        const deltaY = Math.abs(event.changedTouches[0].screenY - this.touchStartY);
        if (deltaX > deltaY) {
            event.preventDefault();
        }
    }

    public onTouchEnd(event: TouchEvent) {
        const deltaX = event.changedTouches[0].screenX - this.touchStartX;
        const threshold = 50;
        if (deltaX < -threshold) {
            this.browsePictures('next');
        } else if (deltaX > threshold) {
            this.browsePictures('previous');
        }
        this.selectedValue = this.images[this.showPictureIndex];
    }

    public browsePictures(directionOrKey: string, event?: KeyboardEvent) {
        const last = this.images.length - 1;
        if (directionOrKey === 'key') {
            if (event.key === 'ArrowLeft') {
                this.showPictureIndex = this.showPictureIndex > 0 ? this.showPictureIndex - 1 : last;
            } else if (event.key === 'ArrowRight') {
                this.showPictureIndex = this.showPictureIndex < last ? this.showPictureIndex + 1 : 0;
            }
        } else {
            if (directionOrKey === 'next') {
                this.showPictureIndex = this.showPictureIndex < last ? this.showPictureIndex + 1 : 0;
            } else if (directionOrKey === 'previous') {
                this.showPictureIndex = this.showPictureIndex > 0 ? this.showPictureIndex - 1 : last;
            }
        }
        this.selectedValue = this.images[this.showPictureIndex];
    }

    public openPictureDialog() {
        this.dialogRef = this.dialog.open(ZoomPictureDialogComponent, {
            data: {
                image: this.images[this.showPictureIndex],
                images: this.images,
                showPictureIndex: this.showPictureIndex
            },
            panelClass: 'full-image-dialog',
            maxWidth: '100vw',
            maxHeight: '100vh',
            height: '100vh',
            width: '100vw'
        });

        this.dialogRef.afterClosed().subscribe((result) => {
            if (result !== undefined) {
                this.showPictureIndex = 0;
                this.selectedValue = this.images[this.showPictureIndex];
            }
        });
    }

    public selectPicture(index: number) {
        this.showPictureIndex = index;
    }

    private buildFileList() {
        const mediaData: any[] = this.data.self.relationships.field_file_media.data;
        return mediaData
            .map(file => this.data.full.includedMap.get(file.id))
            .filter(file => file)
            .map(file => this.data.full.includedMap.get(file.relationships.field_media_file.data.id))
            .filter(file => file)
            .map(file => ({
                name: file.attributes.filename,
                nameEncoded: encodeURIComponent(file.attributes.filename),
                icon: this.resolveIcon(file.attributes.filemime),
                path: file.attributes.uri.url, // Not actually url, just a path on the server.
                pathEncoded: encodeURIComponent(file.attributes.uri.url),
                size: file.attributes.filesize
            } as FileEntry));
    }

    private resolveIcon(mimeType: string) {
        switch (mimeType) {
            case 'text/plain': // txt
            case 'application/rtf': // rtf
            case 'application/msword': // doc
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // docx
                return 'description';

            case 'application/pdf': // pdf
                return 'picture_as_pdf';

            case 'image/png': // png
            case 'image/jpeg': // jpg
            case 'image/gif': // gif
                return 'image';

            case 'application/vnd.ms-excel': // xsl
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': // xslx
                return 'difference';

            case 'application/vnd.ms-powerpoint': // ppt
            case 'application/vnd.openxmlformats-officedocument.presentationml.presentation': // pptx
                return 'insert_chart';

            default:
                return 'insert_drive_file';
        }
    }


    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
